import {
  assert_equal,
  assert_false,
  assert_instanceof,
  assert_is_safe_number,
  assert_not_null,
  assert_number_gte_0,
  assert_number_lt,
  assert_true,
} from '@shared/asserts/utils_asserts';
import { DTO__base } from '@shared/common/DTO__base';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { MAP__fields_by_id } from '@shared/types/types';
import { DTO_META__field_info } from '../../_fields/_base/DTO_META__field_info';
import { FIELD__base } from '../../_fields/_base/FIELD__base';
import { FIELD_PRIMITIVE__base } from '../../_fields/_primitives/_base/FIELD_PRIMITIVE__base';
import { FIELD__list_base_generic } from '../../_fields/_primitives/list/FIELD__list_base_generic';
import { FIELD__any } from '../../_fields/any/FIELD__any';
import { FN_FIELD__get_by_longest_path_match } from '../../_invocations/_base/_methods/FN_FIELD__get_by_longest_path_match';
import { DTO_GRAPH__edge } from '../DTO_GRAPH__edge';
import { DTO_GRAPH__instance } from '../DTO_GRAPH__instance';

declare module '../DTO_GRAPH__instance' {
  interface DTO_GRAPH__instance {
    /*
     * INIT
     */
    (field: FIELD__base): FIELD__base;

    /*
     * REPLACE
     */

    field__replace<T extends FIELD__base>(field_new: T): void;

    /*
     * GET
     */
    field__get(field_meta: DTO_META__field_info): FIELD__base;

    field__get_by_id(field_id: string): FIELD__base;

    field__get_by_id_or_stub(field_id: string): FIELD__base;

    field__get_by_role(field_role: string): FIELD__base;

    field__get_parent(field_meta: DTO_META__field_info): DTO__base;

    //key - field id
    fields__get_map(): MAP__fields_by_id;

    //key - field hash
    fields__get_map_by_hash(): MAP__serializable<string, FIELD__base[]>;

    fields__get_by_type<T extends FIELD__base>(target_type: new (...args: any[]) => T): T[];

    //key - field hash
    fields__get_by_hashes(fields_hashes: string[]): MAP__serializable<string, FIELD__base[]>;

    field__get_primitive(field_meta: DTO_META__field_info): FIELD_PRIMITIVE__base;

    fields__ui_visible(): FIELD__base[];

    /*
     * CONNECTED
     */
    //key - id
    fields__get_connected_all(field: FIELD__base): MAP__fields_by_id;

    //key - id
    fields__get_connected_from(field: FIELD__base): MAP__fields_by_id;

    //key - id
    fields__get_connected_to<T extends FIELD__base>(field: T): MAP__fields_by_id;

    //key - id
    fields__get_connected_base(field: FIELD__base, search_by_fields_to: boolean): MAP__fields_by_id;

    /*
     * GET SOURCE
     */
    field__get_source_for(field: FIELD__base): FIELD__base;

    field__get_source__internal(field: FIELD__base, fields_map: MAP__fields_by_id): FIELD__base;
  }
}

export function INIT_EXTENSION_DTO_GRAPH__instance_fields() {
  // do nothing
}

/*
 * REPLACE
 */

DTO_GRAPH__instance.prototype.field__replace = function <T extends FIELD__base>(field_new: T) {
  assert_false(field_new.meta.data__is_linked);

  /*
   *
   */

  let parent_object = this.field__get_parent(field_new.meta);

  if (parent_object instanceof FIELD__list_base_generic) {
    assert_true(field_new.meta.field_name.includes(':'));
    let [parent_name, index_string] = field_new.meta.field_name.split(':');
    assert_equal(parent_name, parent_object.meta.field_name);

    let index = parseInt(index_string);
    assert_is_safe_number(index);

    assert_number_gte_0(index);
    assert_number_lt(index, parent_object.items.length);

    parent_object.items[index] = field_new;
  } else {
    (parent_object as any)[field_new.meta.field_name] = field_new;
  }

  let node = this.node__get(field_new.meta.node_id);

  node.field__on_changed_after(this, field_new);
};

DTO_GRAPH__instance.prototype.field__get = function (field_meta: DTO_META__field_info) {
  assert_equal(field_meta.graph_id, this.meta.graph_id);

  let node = this.node__get(field_meta.node_id);

  return node.field__get_by_path(field_meta);
};

DTO_GRAPH__instance.prototype.field__get_by_id = function (field_id: string) {
  let fields_map = this.fields__get_map();

  return fields_map.get_or_assert(field_id);
};

DTO_GRAPH__instance.prototype.field__get_by_id_or_stub = function (field_id: string) {
  let fields_map = this.fields__get_map();

  if (fields_map.has(field_id)) return fields_map.get_or_assert(field_id);

  return FIELD__any.CREATE_STUB();
};

DTO_GRAPH__instance.prototype.field__get_by_role = function (field_role: string) {
  let fields_map = this.fields__get_map();

  let out = FIELD__any.CREATE_STUB();

  for (let field of fields_map.values()) {
    if (field.meta.field_role != field_role) continue;

    out = field;
    break;
  }

  return out;
};

DTO_GRAPH__instance.prototype.field__get_parent = function (field_meta: DTO_META__field_info) {
  assert_equal(field_meta.graph_id, this.meta.graph_id);

  let node = this.node__get(field_meta.node_id);

  return node.field__get_parent_by_path(field_meta);
};

//key - field id
DTO_GRAPH__instance.prototype.fields__get_map = function (): MAP__fields_by_id {
  let out: MAP__fields_by_id = new MAP__serializable();

  for (let node of this.nodes__get_all()) {
    let fields_node = node.get_fields_map(true);

    out.merge_with(fields_node);
  }

  return out;
};

DTO_GRAPH__instance.prototype.fields__get_map_by_hash = function () {
  let out = new MAP__serializable<string, FIELD__base[]>();

  let fields_map = this.fields__get_map();

  for (let field of fields_map.values()) {
    out.add_item_to_array(field.meta.field_hash__value_full, field);
  }

  return out;
};

DTO_GRAPH__instance.prototype.fields__get_by_type = function <T extends FIELD__base>(target_type: new (...args: any[]) => T): T[] {
  let out: T[] = [];

  for (let node of this.nodes__get_all()) {
    out = [...out, ...node.fields__get_by_type(target_type)];
  }

  return out;
};

DTO_GRAPH__instance.prototype.fields__get_by_hashes = function (fields_hashes: string[]) {
  fields_hashes = fields_hashes.unique_values();

  let out = new MAP__serializable<string, FIELD__base[]>();

  let fields_map = this.fields__get_map();

  for (let field of fields_map.values()) {
    if (!fields_hashes.includes(field.meta.field_hash__value_full)) continue;

    out.add_item_to_array(field.meta.field_hash__value_full, field);
  }

  return out;
};

DTO_GRAPH__instance.prototype.field__get_primitive = function (field_meta: DTO_META__field_info) {
  let out = this.field__get(field_meta);

  assert_instanceof(out, FIELD_PRIMITIVE__base);

  return out;
};

DTO_GRAPH__instance.prototype.fields__ui_visible = function (): FIELD__base[] {
  let out = this.fields__get_map().values();

  out = out.filter(field => field.meta.ui__has_component);
  out = out.filter(field => field.meta.ui__is_visible);

  return out;
};

/*
 * CONNECTED
 */

DTO_GRAPH__instance.prototype.fields__get_connected_all = function (field_target: FIELD__base): MAP__fields_by_id {
  let out: MAP__fields_by_id = new MAP__serializable();

  let map_connected_to = this.fields__get_connected_to(field_target);
  let map_connected_from = this.fields__get_connected_from(field_target);

  out.merge_with(map_connected_to);
  out.merge_with(map_connected_from);

  return out;
};

DTO_GRAPH__instance.prototype.fields__get_connected_to = function (field_target: FIELD__base): MAP__fields_by_id {
  return this.fields__get_connected_base(field_target, false);
};

DTO_GRAPH__instance.prototype.fields__get_connected_from = function (field_target: FIELD__base): MAP__fields_by_id {
  return this.fields__get_connected_base(field_target, true);
};

//key - id
DTO_GRAPH__instance.prototype.fields__get_connected_base = function (field_target: FIELD__base, search_connected_from: boolean): MAP__fields_by_id {
  const out: MAP__fields_by_id = new MAP__serializable();
  const visited: Set<string> = new Set();
  const edges_queue: DTO_GRAPH__edge[] = [];

  let id_target = field_target.meta.field_id;

  visited.add(id_target);

  let filter_target = search_connected_from ? DTO_GRAPH__edge.filter_by_id_from_feld(id_target) : DTO_GRAPH__edge.filter_by_id_to_feld(id_target);

  let edges_initial = this.edges.filter(filter_target);

  edges_queue.push(...edges_initial);

  while (edges_queue.length > 0) {
    const edge = edges_queue.shift();
    assert_not_null(edge);

    const id_node_connected = search_connected_from ? edge.field_to__node_id : edge.field_from__node_id;
    const id_field_connected = search_connected_from ? edge.field_to__field_id : edge.field_from__field_id;

    if (visited.has(id_field_connected)) continue; //already visited

    const node_connected = this.node__get(id_node_connected);
    const field_found = node_connected.field__get(id_field_connected);

    //add found node
    visited.add(id_field_connected);
    out.set(id_field_connected, field_found);

    //find next edges
    let filter_found = search_connected_from
      ? DTO_GRAPH__edge.filter_by_id_from_feld(id_field_connected)
      : DTO_GRAPH__edge.filter_by_id_to_feld(id_field_connected);

    const edges_next = this.edges.filter(filter_found);
    edges_queue.push(...edges_next);
  }

  return out;
};

DTO_GRAPH__instance.prototype.field__get_source_for = function (field: FIELD__base): FIELD__base {
  if (this.cache__fields_map_of_sources.has(field.meta.field_id)) return this.cache__fields_map_of_sources.get_or_assert(field.meta.field_id);

  let fields_map = this.fields__get_map();

  let out = this.field__get_source__internal(field, fields_map);

  return out;
};

DTO_GRAPH__instance.prototype.field__get_source__internal = function (field_target: FIELD__base, fields_map: MAP__fields_by_id): FIELD__base {
  assert_true(field_target.meta.data__is_linked);

  let { data__synced_by_parent_id } = field_target.meta;

  let out: FIELD__base;

  if (!data__synced_by_parent_id.is_empty_uuid()) {
    let parent = fields_map.get_or_assert(data__synced_by_parent_id);

    let source_id_parent = this.fields_ids_map__by_target_id.get_or_assert(parent.meta.field_id);

    let parent_source = fields_map.get_or_assert(source_id_parent);

    out = FN_FIELD__get_by_longest_path_match(parent_source, field_target.meta.field_path);
  } else {
    let source_id = this.fields_ids_map__by_target_id.get_or_assert(field_target.meta.field_id);

    out = fields_map.get_or_assert(source_id);
  }

  /*
   *
   */

  if (out.meta.data__is_linked) {
    out = this.field__get_source__internal(out, fields_map);
  } else {
    assert_false(this.fields_ids_map__by_target_id.has(out.meta.field_id));
  }
  return out;
};
